import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { resetPassword, forgetPassword } from "../../Actions/LoginAction";
import { handleDangerVisible, handleVisibleForEdit } from "../../global/Utils";

const CreateNewPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const isPasswordResetting = useSelector(
    (state) => state.LoginReducer.isPasswordResetting,
  );

  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [cPassword, setCPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [errorPassword, setErrorPassword] = useState(false);
  const [helperTextPassword, setHelperTextPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showCPassword, setShowCPassword] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [timer, setTimer] = useState(120);

  // Password Validation
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push("Must be at least 8 characters");
    if (!/[A-Z]/.test(password))
      errors.push("Must contain an uppercase letter");
    if (!/[a-z]/.test(password)) errors.push("Must contain a lowercase letter");
    if (!/[0-9]/.test(password)) errors.push("Must contain a number");
    if (!/[!@#$%^&*()_+[\]{}<>?]/.test(password))
      errors.push("Must contain a special character");
    return errors;
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordErrors(validatePassword(value));
  };

  const handleConfirmPassword = (e) => {
    const value = e.target.value;
    setCPassword(value);

    if (password && value && password !== value) {
      setErrorPassword(true);
      setHelperTextPassword("Passwords do not match");
    } else {
      setErrorPassword(false);
      setHelperTextPassword("");
    }
  };

  const handleSubmit = () => {
    dispatch(
      resetPassword(
        {
          _id: location?.state?.id,
          password,
          code,
        },
        handleVisibleForEdit,
        handleDangerVisible,
      ),
    );
  };

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  useEffect(() => {
    let interval;
    if (resendDisabled) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendDisabled]);

  useEffect(() => {
    const id = location?.state?.id;
    const email = location?.state?.email;

    if (!id || !email) {
      navigate("/forgot-password", { replace: true });
    }
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6">
        <div className="text-center mb-6">
          <p className="text-sm text-gray-500">Ebeaconsofts Digital Invoicing</p>
          <h1 className="text-xl font-semibold text-gray-800 mt-1">
            Change Your Password
          </h1>
          <p className="text-sm text-gray-500 mt-2 leading-relaxed">
            We've sent a verification code to your email. Please check your
            inbox (including spam) and enter the code below to reset your
            password.
          </p>
        </div>
        <div className="space-y-2">
          <div>
            <label className="text-sm font-medium text-gray-700">Code</label>
            <input
              type="number"
              placeholder="Enter Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="mt-1 w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
            />
            <div className="flex justify-end mt-1">
              <button
                type="button"
                disabled={resendDisabled}
                onClick={() => {
                  dispatch(
                    forgetPassword(
                      { email: location?.state?.email },
                      () => {},
                      handleDangerVisible,
                    ),
                  );
                  setResendDisabled(true);
                  setTimer(120);
                }}
                className={`text-xs ${
                  resendDisabled
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-blue-600 hover:underline"
                }`}
              >
                {resendDisabled
                  ? `Resend in ${formatTime(timer)}`
                  : "Resend Code"}
              </button>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="********"
                value={password}
                onChange={handlePasswordChange}
                className={`mt-1 w-full px-3 py-2 border rounded-md outline-none focus:ring-2 ${
                  passwordErrors.length > 0
                    ? "border-red-500 focus:ring-red-500"
                    : "focus:ring-blue-500"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2 text-sm text-gray-500"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {passwordErrors.map((err, i) => (
              <p key={i} className="text-red-500 text-xs mt-1">
                {err}
              </p>
            ))}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showCPassword ? "text" : "password"}
                placeholder="********"
                value={cPassword}
                onChange={handleConfirmPassword}
                className={`mt-1 w-full px-3 py-2 border rounded-md outline-none focus:ring-2 ${
                  errorPassword
                    ? "border-red-500 focus:ring-red-500"
                    : "focus:ring-blue-500"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowCPassword(!showCPassword)}
                className="absolute right-3 top-2 text-sm text-gray-500"
              >
                {showCPassword ? "Hide" : "Show"}
              </button>
            </div>

            {helperTextPassword && (
              <p className="text-red-500 text-xs mt-1">{helperTextPassword}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-3">
            <button
              onClick={() => navigate("/login")}
              className="w-full sm:w-1/2 py-2 rounded-md text-sm text-blue-600 border border-blue-600 hover:bg-blue-50 transition"
            >
              Back to Login
            </button>

            <button
              onClick={handleSubmit}
              disabled={
                !code ||
                !password ||
                !cPassword ||
                errorPassword ||
                passwordErrors.length > 0
              }
              className={`w-full sm:w-1/2 py-2 rounded-md text-white transition ${
                !code ||
                !password ||
                !cPassword ||
                errorPassword ||
                passwordErrors.length > 0
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isPasswordResetting ? "Processing..." : "Reset Password"}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            Powered by:{" "}
            <a
              href="https://ebeaconsofts.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-600 transition"
            >
              EBEACONSOFTS SMC PVT LTD
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateNewPassword;
